import React, { useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Users, Clock, TrendingUp, Plus, X, Search } from 'lucide-react';
import AppShell from '../components/layout/AppShell';
import DiscussionCard from '../features/panels/components/DiscussionCard';
import HotDiscussions from '../features/panels/components/HotDiscussions';
import TrendingTopics from '../features/panels/components/TrendingTopics';
import CreateDiscussionForm from '../features/panels/components/CreateDiscussionForm';
import PanelMembersSideSheet from '../features/panels/components/PanelMembersSideSheet';
import {
  usePanelById,
  useDiscussions,
  useToggleLike,
  useHotDiscussions,
  useTrendingTopics,
} from '../features/panels/hooks/use-panels';
import { useQueryClient } from '@tanstack/react-query';

const PanelPage = () => {
  const { panelId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const queryClient = useQueryClient();
  const [sort, setSort] = useState('recent');
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isMembersSheetOpen, setIsMembersSheetOpen] = useState(false);
  const createFormRef = React.useRef(null);

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    setSearchParams(newParams);
  };
  
  const handleOpenCreateForm = () => {
    setSelectedDiscussion(null); // Go back to feed if viewing a single discussion
    setShowCreateForm(true);
    // Use a slight timeout to allow the form to render before scrolling
    setTimeout(() => {
      createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };
  
  const handleShowLess = () => {
    queryClient.setQueryData(['discussions', panelId, sort, selectedTag, q], (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: [data.pages[0]],
        pageParams: [data.pageParams[0]],
      };
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Mobile expansion states
  const [isMobileTrendingExpanded, setIsMobileTrendingExpanded] = useState(false);
  const [isMobileHotExpanded, setIsMobileHotExpanded] = useState(false);

  const { data: panelData, isLoading: isPanelLoading, error: panelError } = usePanelById(panelId);
  const { 
    data: discussionsData, 
    isLoading: isDiscussionsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useDiscussions(panelId, sort, selectedTag, q);
  const { data: hotData } = useHotDiscussions(panelId);
  const { data: trendingData } = useTrendingTopics(panelId);
  const { mutate: toggleLikeMutation } = useToggleLike();

  const panel = panelData;                              
  const discussions = discussionsData?.pages?.flatMap(p => p.data) || [];       
  const hotDiscussions = hotData || [];                  
  const trendingTopics = trendingData || [];

  const handleLike = (discussion) => {
    toggleLikeMutation({ panelId, discussionId: discussion.id });
  };

  if (isPanelLoading) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b46c1]" />
        </div>
      </AppShell>
    );
  }

  if (panelError) {
    const isForbidden = panelError.response?.status === 403;
    return (
      <AppShell>
        <div className="flex-1 flex flex-col items-center justify-center py-20">
          <p className="text-red-500 font-semibold text-lg mb-2">
            {isForbidden ? "Access Denied" : "Error loading panel"}
          </p>
          <p className="text-gray-500 text-center max-w-md">
            {isForbidden 
              ? "You must apply to this opportunity to view the discussion panel." 
              : panelError.response?.data?.message || panelError.message}
          </p>
          <Link to="/feed" className="mt-6 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-medium">
            Return to Feed
          </Link>
        </div>
      </AppShell>
    );
  }

  if (!panel) {
    return (
      <AppShell>
        <div className="flex-1 flex flex-col items-center justify-center py-20">
          <p className="text-gray-500">Panel not found.</p>
          <Link to="/feed" className="mt-4 text-blue-600 hover:underline">
            Return to Feed
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-8 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link to="/feed" className="text-blue-600 hover:underline">Vet Collab</Link>
          <span>/</span>
          <span className="text-gray-700 font-medium">{panel.name}</span>
        </div>

        {/* Panel Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">{panel.name}</h1>
            <button 
              onClick={() => setIsMembersSheetOpen(true)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#6b46c1] hover:bg-purple-50 px-2 py-1 rounded-md transition-colors cursor-pointer"
            >
              <Users size={15} /> {panel._count?.members || 0} members
            </button>
          </div>
          {panel.description && (
            <p className="text-sm text-gray-500 leading-relaxed max-w-3xl">
              {panel.description}
            </p>
          )}
        </div>

        {/* Main Content + Sidebar */}
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            {/* Mobile-Only Top Section */}
            <div className="lg:hidden mb-6 space-y-5">
              {/* Top Action */}
              <div>
                <button
                  onClick={handleOpenCreateForm}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6b46c1] hover:bg-[#553098] text-white text-xs font-semibold rounded-md shadow-sm transition-colors"
                >
                  <Plus size={14} /> New Discussion
                </button>
              </div>

              {/* Trending Topics Horizontal */}
              {trendingTopics.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <TrendingUp size={12} className="text-green-500" /> Trending
                  </h3>
                  <div className="flex overflow-x-auto gap-2 pb-1 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                    {(isMobileTrendingExpanded ? trendingTopics : trendingTopics.slice(0, 4)).map(t => (
                      <button 
                        key={t.tag}
                        onClick={() => setSelectedTag(t.tag)}
                        className="flex-shrink-0 px-3 py-1.5 bg-white border border-gray-200 text-blue-600 text-xs font-medium rounded-full shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        #{t.tag} <span className="text-gray-400 ml-1">{t.count}</span>
                      </button>
                    ))}
                    {!isMobileTrendingExpanded && trendingTopics.length > 4 && (
                      <button
                        onClick={() => setIsMobileTrendingExpanded(true)}
                        className="flex-shrink-0 px-3 py-1.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full shadow-sm hover:bg-blue-100 transition-colors"
                      >
                        +{trendingTopics.length - 4} More
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Hot Discussions Horizontal */}
              {hotDiscussions.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    🔥 Hot Discussions
                  </h3>
                  <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                    {(isMobileHotExpanded ? hotDiscussions : hotDiscussions.slice(0, 4)).map(d => (
                      <button 
                        key={d.id}
                        onClick={() => setSelectedDiscussion(d)}
                        className="flex-shrink-0 w-60 bg-white border border-orange-100 p-3 rounded-xl shadow-sm text-left hover:border-orange-200 transition-colors"
                      >
                        <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">{d.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">{d._count?.comments || 0} replies</p>
                      </button>
                    ))}
                    {!isMobileHotExpanded && hotDiscussions.length > 4 && (
                      <button
                        onClick={() => setIsMobileHotExpanded(true)}
                        className="flex-shrink-0 flex items-center justify-center w-20 bg-orange-50 border border-orange-100 p-3 rounded-xl shadow-sm text-orange-600 text-xs font-semibold hover:bg-orange-100 transition-colors"
                      >
                        View All
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              {selectedDiscussion ? (
                <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
                <div>
                  <button 
                    onClick={() => setSelectedDiscussion(null)} 
                    className="mb-6 flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    &larr; Back to discussions
                  </button>
                  <DiscussionCard
                    discussion={selectedDiscussion}
                    panelId={panelId}
                    onLike={handleLike}
                    defaultShowComments={true}
                  />
                </div>
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {q && (
                    <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-indigo-900">
                        <Search size={16} className="text-indigo-600" />
                        <span>Showing discussions matching "<span className="font-semibold">{q}</span>"</span>
                      </div>
                      <button
                        onClick={clearSearch}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                      >
                        <X size={14} /> Clear search
                      </button>
                    </div>
                  )}

                  {/* Form Box */}
                  {showCreateForm && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" ref={createFormRef}>
                      <CreateDiscussionForm 
                        panelId={panelId} 
                        isExpanded={showCreateForm} 
                        setIsExpanded={setShowCreateForm} 
                      />
                    </div>
                  )}
                  
                  {/* Feed Box */}
                  <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
                    {/* Discussions Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                    <h2 className="text-lg font-bold text-gray-900">Recent Discussions</h2>
                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 self-start sm:self-auto">
                      <button
                        onClick={() => setSort('recent')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          sort === 'recent' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Clock size={13} /> Recent
                      </button>
                      <button
                        onClick={() => setSort('trending')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          sort === 'trending' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <TrendingUp size={13} /> Trending
                      </button>
                    </div>
                  </div>

                  {/* Active Tag Filter */}
                  {selectedTag && (
                    <div className="mb-4 flex items-center justify-between bg-blue-50 text-blue-700 px-4 py-2.5 rounded-lg border border-blue-100">
                      <span className="text-sm font-medium">
                        Showing discussions tagged with <strong>#{selectedTag}</strong>
                      </span>
                      <button 
                        onClick={() => setSelectedTag(null)}
                        className="flex items-center gap-1 text-sm font-semibold hover:text-blue-900 transition-colors bg-white/50 px-2 py-1 rounded-md"
                      >
                        <X size={14} /> Clear Filter
                      </button>
                    </div>
                  )}

                  {/* Discussion List */}
                  {isDiscussionsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6b46c1]" />
                    </div>
                  ) : discussions.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-sm mb-4">No discussions found.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-4 bg-gray-50/70 p-3 sm:p-5 rounded-xl border border-gray-100 shadow-inner -mx-2 sm:-mx-4">
                        {discussions.map((d) => (
                          <div key={d.id} className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)] border border-gray-200 transition-all hover:shadow-md hover:border-gray-300">
                            <DiscussionCard
                              discussion={d}
                              panelId={panelId}
                              onLike={handleLike}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Pagination Controls */}
                      {(hasNextPage || discussionsData?.pages?.length > 1) && (
                        <div className="flex items-center justify-center gap-4 pt-4">
                          {discussionsData?.pages?.length > 1 && (
                            <button
                              onClick={handleShowLess}
                              className="text-sm font-semibold text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Show Less
                            </button>
                          )}
                          
                          {hasNextPage && (
                            <button
                              onClick={() => fetchNextPage()}
                              disabled={isFetchingNextPage}
                              className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                            >
                              {isFetchingNextPage ? 'Loading more...' : 'Load More Discussions'}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

          {/* Right: Sidebar (Desktop Only) */}
          <div className="hidden lg:block w-72 flex-shrink-0 space-y-5">
            {/* New Discussion Button */}
            <button
              onClick={handleOpenCreateForm}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#6b46c1] rounded-lg hover:bg-[#553098] transition-colors shadow-sm"
            >
              <Plus size={16} /> New Discussion
            </button>

            <HotDiscussions 
              discussions={hotDiscussions} 
              onView={(d) => setSelectedDiscussion(d)} 
            />
            <TrendingTopics 
              topics={trendingTopics} 
              onSelect={(tag) => setSelectedTag(tag)} 
            />
          </div>
        </div>
      </div>

      <PanelMembersSideSheet 
        isOpen={isMembersSheetOpen}
        onClose={() => setIsMembersSheetOpen(false)}
        panelId={panel.id}
        panelName={panel.name}
        memberCount={panel._count?.members || 0}
      />
    </AppShell>
  );
};

export default PanelPage;
